import {ComponentType} from 'react';
import moment, {Moment} from 'moment';
import {Box, styled} from '@mui/material';
import {DateCalendar, PickersDay, PickersDayProps} from '@mui/x-date-pickers';
import {useTranslation} from 'react-i18next';
import {Button} from '../button/button';
import {translations} from '../../i18n/translations';

export type TimeRangePickerProps = {
  start?: Moment;
  end?: Moment;
  className?: string;
  maxDate?: Moment;
  showDaysOutsideCurrentMonth?: boolean;
  disableFuture?: boolean;
  onChange?: (date: Moment) => void;
};

export function DateRangeCalendar({start, end, ...rest}: TimeRangePickerProps) {
  const {t} = useTranslation();

  return (
    <Box sx={{position: 'relative'}}>
      <DateCalendar
        value={start}
        slots={{
          day: Day,
          leftArrowIcon: () => null,
          rightArrowIcon: () => null,
        }}
        slotProps={{
          day: (ownerState) => ({
            start: start,
            end: end,
            onPointerEnter: undefined,
            onPointerLeave: undefined,
          }),
        }}
        {...rest}
      />
      <Button
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          margin: '13px 20px 8px 0',
        }}
        onClick={() => {
          rest.onChange?.(moment());
        }}
      >
        {t(translations.date_time_picker.today)}
      </Button>
    </Box>
  );
}

// -----------------------------------------------------------------------
// -----------------------------------------------------------------------
// -----------------------------------------------------------------------

function Day({
  day,
  start,
  end,
  onEnded,
  ...rest
}: PickersDayProps<Moment> & {
  start?: Moment;
  end?: Moment;
}) {
  return (
    <CustomPickersDay
      day={day}
      sx={{px: 2.5}}
      disableMargin
      isSelected={isDaySelected(day, start, end)}
      start={start}
      end={end}
      {...rest}
    />
  );
}

const isDaySelected = (day: Moment, start?: Moment, end?: Moment): boolean => {
  return day.isBetween(start, end);
};

// -----------------------------------------------------------------------
// -----------------------------------------------------------------------

interface CustomPickerDayProps extends PickersDayProps<Moment> {
  isSelected: boolean;
  start?: Moment;
  end?: Moment;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'range',
})<CustomPickerDayProps>(({theme, isSelected, day, start, end}) => ({
  borderRadius: 0,
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main,
    },
  }),
  ...(day.isSame(start, 'day') && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(day.isSame(end, 'day') && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
})) as ComponentType<CustomPickerDayProps>;
